import { z } from 'zod';
import {
  ALERT_REASONS,
  ALERT_RESOLUTIONS,
  ALERT_STATES,
  CHECK_ERROR_CODES,
  CHECK_KINDS,
  CHECK_STATUSES,
  DELIVERY_ERROR_CODES,
  DELIVERY_STATUSES,
  NOTIFICATION_KINDS,
  ROLES,
  MONITORING,
  SERVICE_TYPES,
  WORKER_STATES,
} from '@bekebi/domain';
import { uuidSchema } from '../common';
import { pageQuerySchema, pageSchema } from './pagination';

/**
 * Scheduled service checks (technical layer). Readable with `monitoring.view` and changed with
 * `monitoring.manage` — both Admin only. Operations sees only the derived per-service health on
 * the service layer. No check carries or accepts credentials.
 */

/** A DNS host name or an IP address; no scheme, port or path. */
const hostSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(1)
  .max(253)
  .refine(
    (v) =>
      z.ipv4().safeParse(v).success ||
      z.ipv6().safeParse(v).success ||
      /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*$/.test(
        v,
      ),
    { message: 'must be a host name or IP address' },
  );
const portSchema = z.number().int().min(1).max(65535);
const httpsUrlSchema = z
  .string()
  .trim()
  .max(2048)
  .refine(
    (v) => {
      try {
        const url = new URL(v);
        return (
          url.protocol === 'https:' && !url.username && !url.password && !url.search && !url.hash
        );
      } catch {
        return false;
      }
    },
    { message: 'must be an https:// URL without credentials, query or fragment' },
  );

/** Per-kind target. The Website check has none: it uses the Website's public address. */
export const CHECK_TARGET_SCHEMAS = {
  website_https: z.strictObject({}),
  nextcloud_status: z.strictObject({ baseUrl: httpsUrlSchema }),
  smtp_tls: z.strictObject({
    host: hostSchema,
    port: portSchema,
    security: z.enum(['implicit', 'starttls']),
  }),
  imap_tls: z.strictObject({ host: hostSchema, port: portSchema }),
  /** The mail domain (not an IP address) and its DKIM selector (Mailu's default: "dkim"). */
  mail_dns: z.strictObject({
    domain: hostSchema.refine((v) => /[a-z]/.test(v) && !v.includes(':'), {
      message: 'must be a domain name',
    }),
    dkimSelector: z
      .string()
      .trim()
      .toLowerCase()
      .regex(/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/, { message: 'must be a DKIM selector' })
      .default('dkim'),
  }),
} as const;

const intervalSchema = z
  .number()
  .int()
  .min(MONITORING.minIntervalSeconds)
  .max(MONITORING.maxIntervalSeconds);

const createFor = <K extends keyof typeof CHECK_TARGET_SCHEMAS>(kind: K) =>
  z.strictObject({
    serviceId: uuidSchema,
    kind: z.literal(kind),
    target: CHECK_TARGET_SCHEMAS[kind],
    intervalSeconds: intervalSchema.optional(),
    enabled: z.boolean().optional(),
  });

export const monitoringCheckCreateSchema = z.discriminatedUnion('kind', [
  createFor('website_https'),
  createFor('nextcloud_status'),
  createFor('smtp_tls'),
  createFor('imap_tls'),
  createFor('mail_dns'),
]);
export type MonitoringCheckCreate = z.infer<typeof monitoringCheckCreateSchema>;

/** The target is re-validated against the check's own kind by the route. */
export const monitoringCheckUpdateSchema = z
  .strictObject({
    target: z.record(z.string(), z.unknown()).optional(),
    intervalSeconds: intervalSchema.optional(),
    enabled: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'nothing to change' });
export type MonitoringCheckUpdate = z.infer<typeof monitoringCheckUpdateSchema>;

export const monitoringCheckSchema = z.object({
  id: uuidSchema,
  serviceId: uuidSchema,
  kind: z.enum(CHECK_KINDS),
  /** What the check proves (the mail checks are availability, not delivery). */
  description: z.string(),
  target: z.union([
    CHECK_TARGET_SCHEMAS.nextcloud_status,
    CHECK_TARGET_SCHEMAS.smtp_tls,
    CHECK_TARGET_SCHEMAS.imap_tls,
    CHECK_TARGET_SCHEMAS.mail_dns,
    CHECK_TARGET_SCHEMAS.website_https,
  ]),
  intervalSeconds: z.number().int(),
  enabled: z.boolean(),
  lastCheckedAt: z.string().nullable(),
  lastStatus: z.enum(CHECK_STATUSES).nullable(),
  /** True when the newest result is older than the expected window; it is not current. */
  stale: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type MonitoringCheck = z.infer<typeof monitoringCheckSchema>;

export const monitoringCheckPageSchema = pageSchema(monitoringCheckSchema);

export const monitoringCheckListQuerySchema = pageQuerySchema.extend({
  serviceId: uuidSchema.optional(),
});

export const monitoringCheckParamsSchema = z.object({ checkId: uuidSchema });

export const monitoringCheckResultSchema = z.object({
  id: uuidSchema,
  status: z.enum(CHECK_STATUSES),
  checkedAt: z.string(),
  durationMs: z.number().int().nullable(),
  errorCode: z.enum(CHECK_ERROR_CODES).nullable(),
  errorMessage: z.string().nullable(),
  details: z.record(z.string(), z.unknown()),
});
export type MonitoringCheckResult = z.infer<typeof monitoringCheckResultSchema>;

export const monitoringCheckResultPageSchema = pageSchema(monitoringCheckResultSchema);

/**
 * Monitoring alerts (Phase 07 Stage 02; technical layer, `monitoring.view`). Opened and resolved
 * by the worker from derived service health; there is no endpoint that creates, changes or
 * resolves an alert. No check target, credential or provider detail is included.
 */
export const monitoringAlertSchema = z.object({
  id: uuidSchema,
  service: z.object({ id: uuidSchema, type: z.enum(SERVICE_TYPES), name: z.string() }),
  state: z.enum(ALERT_STATES),
  reason: z.enum(ALERT_REASONS),
  openedAt: z.string(),
  resolvedAt: z.string().nullable(),
  resolution: z.enum(ALERT_RESOLUTIONS).nullable(),
});
export type MonitoringAlert = z.infer<typeof monitoringAlertSchema>;

export const monitoringAlertPageSchema = pageSchema(monitoringAlertSchema);

export const monitoringAlertListQuerySchema = pageQuerySchema.extend({
  state: z.enum(ALERT_STATES).default('open'),
});

export const monitoringAlertParamsSchema = z.object({ alertId: uuidSchema });

/**
 * The monitoring worker's liveness (Phase 07 Stage 03; technical layer, `monitoring.view`), from
 * its heartbeat only. `lastSeenAt` is null exactly when it has never reported.
 */
export const monitoringWorkerStatusSchema = z.object({
  state: z.enum(WORKER_STATES),
  lastSeenAt: z.string().nullable(),
});
export type MonitoringWorkerStatus = z.infer<typeof monitoringWorkerStatusSchema>;

/**
 * Alert email deliveries (Phase 07 Stage 05-C; technical layer, `monitoring.view`, Admin only).
 * `sent` means the mail server ACCEPTED the email — not that it reached an inbox. The recipient
 * is shown by Bekebi User ID and role only, never by email address. `errorCode` is one of the
 * fixed delivery codes, never the mail server's own reply. No SMTP setting is ever included.
 */
export const notificationDeliverySchema = z.object({
  id: uuidSchema,
  kind: z.enum(NOTIFICATION_KINDS),
  status: z.enum(DELIVERY_STATUSES),
  /** Attempts made so far, out of `maxAttempts`. */
  attempts: z.number().int(),
  maxAttempts: z.number().int(),
  errorCode: z.enum(DELIVERY_ERROR_CODES).nullable(),
  recipient: z.object({ bekebiUserId: z.string(), role: z.enum(ROLES) }),
  alert: z.object({
    id: uuidSchema,
    service: z.object({ id: uuidSchema, type: z.enum(SERVICE_TYPES), name: z.string() }),
  }),
  /** When the email was planned. */
  createdAt: z.string(),
  /** When its state last changed. */
  updatedAt: z.string(),
  /** When the mail server accepted it; null unless `status` is `sent`. */
  sentAt: z.string().nullable(),
});
export type NotificationDelivery = z.infer<typeof notificationDeliverySchema>;

/** Whether alert emails are set up on the server, and whether they are failing (see domain). */
const notificationStateSchema = z.object({
  emailSetUp: z.boolean(),
  failing: z.boolean(),
});

export const monitoringNotificationPageSchema = pageSchema(notificationDeliverySchema).extend(
  notificationStateSchema.shape,
);
export type MonitoringNotificationPage = z.infer<typeof monitoringNotificationPageSchema>;

export const monitoringAlertNotificationsSchema = z.object({
  emailSetUp: z.boolean(),
  items: z.array(notificationDeliverySchema),
});
export type MonitoringAlertNotifications = z.infer<typeof monitoringAlertNotificationsSchema>;

/** The test email takes no input: it always goes to the requesting Admin's own address. */
export const testEmailRequestSchema = z.strictObject({}).optional();

/** The mail server accepted the test email (not proof that it reached the inbox). */
export const testEmailResultSchema = z.object({
  result: z.literal('accepted'),
  acceptedAt: z.string(),
});
export type TestEmailResult = z.infer<typeof testEmailResultSchema>;

/**
 * The Portal Server itself (technical layer, `monitoring.view`, Admin only), read live when asked:
 * whether the portal's database answers and its size, and the disk that holds the portal's
 * containers and database volume (read by the API process with statfs). Separate from the Cloud
 * Server and the Mail Server. `disk` is null when it could not be read — never a guessed value.
 * The worker's heartbeat is reported by `/monitoring/worker`.
 */
export const portalServerStatusSchema = z.object({
  checkedAt: z.string(),
  database: z.object({
    state: z.enum(['ok', 'unreachable']),
    /** Null when the database did not answer. */
    sizeBytes: z.number().nullable(),
  }),
  disk: z
    .object({
      totalBytes: z.number(),
      usedBytes: z.number(),
      availableBytes: z.number(),
      /** Used share of the space available to users, as `df` computes it (rounded up). */
      usedPercent: z.number(),
    })
    .nullable(),
});
export type PortalServerStatus = z.infer<typeof portalServerStatusSchema>;
